import { createAsyncThunk } from '@reduxjs/toolkit';
import getJitDataService from 'modules/inbound/programCoverage/application/GetPartCoverageService';
import {
  getCurrentPage,
  getResultsPerPage,
  getSelectedColumn,
  getSelectedConType,
  getSelectedPiece,
  getSelectedProviderTier
} from '.';
import {
  NameValues,
  FiltersItem
} from 'modules/inbound/shared/domain/NameValues';
import { getUsername } from 'modules/shared/infrastructure/redux/UserReducer';
import {
  getFilterSearch,
  getSelectedNeedsSource,
  getSelectedSubheaderFilters
} from '../PartCoverageFiltersReducer';
import DownloadJitStockUseCase from 'modules/inbound/programCoverage/application/DownloadJitStockUseCase';
import {
  ColumnSelected,
  Sort
} from 'modules/shared/layout/components/TableHeader/types';
import EditCommentUseCase from 'modules/inbound/programCoverage/application/EditCommentUseCase';
import GetTransitsService from 'modules/inbound/programCoverage/application/GetTransitsService';
import { format, addDays, parseISO } from 'date-fns';
import EditTransitsService from 'modules/inbound/programCoverage/application/EditTransitsService';
import DeleteTransitsService from 'modules/inbound/programCoverage/application/DeleteTransitsService';
import GetRemarksService from 'modules/inbound/programCoverage/application/GetRemarksService';
import DeleteJitRemarksByIdService from 'modules/inbound/programCoverage/application/DeleteJitRemarksByIdService';
import EditJitRemarkService from 'modules/inbound/programCoverage/application/EditJitRemarkService';
import { RemarkData } from 'modules/inbound/programCoverage/domain/RemarksData';
import CreateJitRemarkService from 'modules/inbound/programCoverage/application/CreateJitRemarkService';
import { RemarkMapper } from 'modules/inbound/programCoverage/infrastructure/mappers/remark.mapper';
import DownloadJitRemarkService from 'modules/inbound/programCoverage/application/DownloadJitRemarkService';
import {
  LoadData,
  LoadDataEdit,
  LoadMapper
} from 'modules/inbound/programCoverage/infrastructure/mappers/load.mapper';
import CreateJitTransitService from 'modules/inbound/programCoverage/application/CreateJitTransitService';
import { loadTransists } from 'modules/inbound/programCoverage/domain/TransitsData';
import GetDateFormatedService from 'modules/inbound/shared/domain/GetDateFormatedService';
import { IDateRange } from 'modules/inbound/endOfProductionDate/domain/IEndOfProductionDateRepository';
import DownloadM100UseCase from 'modules/inbound/programCoverage/application/DownloadJitM100UseCase';
import GetStockStatusService from 'modules/inbound/programCoverage/application/GetStockStatus';

const getSelectedFiltersQueryArray = (state) => {
  const {
    selectedFamily,
    selectedLines,
    selectedProvidersTier1Ids,
    selectedLogisticOperatorsIds,
    selectedModels,
    selectedInbounders,
    selectedFlowType,
    selectedStatus,
    selectedPRs,
    selectedBoardKey,
    selectedCoverageStoppedAt,
    selectedQuantityStock
  } = getSelectedSubheaderFilters(state);

  const fromDate = selectedCoverageStoppedAt.value.fromDate
    ? GetDateFormatedService.formatTimeToFullCustomDate(
        selectedCoverageStoppedAt.value.fromDate
      )
    : '';
  const toDate = selectedCoverageStoppedAt.value.toDate
    ? GetDateFormatedService.formatTimeToFullCustomDate(
        selectedCoverageStoppedAt.value.toDate
      )
    : '';
  const formatedCoverageStoppedAt: IDateRange = { fromDate, toDate };
  const queryFilters: FiltersItem[] = [
    {
      name: NameValues.FAMILY,
      values: selectedFamily
    },
    {
      name: NameValues.LINE,
      values: selectedLines
    },
    {
      name: NameValues.PROVIDER_TIER_1_ID,
      values: selectedProvidersTier1Ids.map(({ key, text }) => ({
        key: key.split('-')[0].trim(),
        text: text
      }))
    },
    {
      name: NameValues.LOGISTIC_OPERATOR_ID,
      values: selectedLogisticOperatorsIds
    },
    {
      name: NameValues.MODELS,
      values: selectedModels
    },
    {
      name: NameValues.INBOUNDER,
      values: selectedInbounders
    },
    {
      name: NameValues.PRS,
      values: selectedPRs
    },
    {
      name: NameValues.COVERAGE_STOPPED_AT,
      values: [
        formatedCoverageStoppedAt.fromDate,
        formatedCoverageStoppedAt.toDate
      ]
    },
    {
      name: NameValues.QUANTITYSTOCK,
      values: [selectedQuantityStock.value.Min, selectedQuantityStock.value.Max]
    }
  ];

  !!selectedFlowType.key &&
    queryFilters.push({
      name: NameValues.FLOW_TYPE,
      values: [selectedFlowType]
    });
  !!selectedStatus.key &&
    queryFilters.push({
      name: NameValues.SEQUENCERCOVERAGESTATUS,
      values: [selectedStatus]
    });

  !!selectedBoardKey.key &&
    queryFilters.push({
      name: NameValues.IS_ON_LOCKS,
      values: [selectedBoardKey]
    });

  let querySearch: string[] = [];

  queryFilters.forEach((filter) => {
    const values = filter.values.map((item) => item?.key ?? item);
    const hasValues = filter.values.length > 0;

    if (!hasValues) return;

    const isLikeFilter =
      filter.name === NameValues.MODELS || filter.name === NameValues.PRS;
    const isInFilter = filter.name === NameValues.LINES;
    const isDateRangeFilter =
      filter.name === NameValues.COVERAGE_STOPPED_AT &&
      !!filter.values[0] &&
      !!filter.values[1];
    const isQuantityStockFilter =
      filter.name === NameValues.QUANTITYSTOCK &&
      !!filter.values[0] &&
      !!filter.values[1];
    if (isDateRangeFilter) {
      const fromDate = filter.values[0];
      const toDate = filter.values[1];
      querySearch.push(
        `coverage_stopped_at:>=:${fromDate}||coverage_stopped_at:<=:${toDate}`
      );
      return;
    }
    if (isQuantityStockFilter) {
      const min = filter.values[0];
      const max = filter.values[1];
      querySearch.push(`stock_quantity:>=:${min}||stock_quantity:<=:${max}`);
      return;
    }
    if (isLikeFilter) {
      querySearch.push(`${filter.name}:LK:(${values.join(';')})`);
      return;
    }

    if (
      (values.length > 1 || isInFilter) &&
      !!filter.values[0] &&
      !!filter.values[1]
    ) {
      querySearch.push(
        `${filter.name}:IN:(${values.map((v) => `"${v}"`).join(';')})`
      );
      return;
    }

    if (values.length === 1) {
      querySearch.push(`${filter.name}:=:"${values[0]}"`);
    }
  });

  return querySearch;
};

const getPartCoverage = createAsyncThunk(
  //'partCoverageData/getPartCoverageData',
  'PartCoverageProgramCoverage/getPartCoverageData',
  async (_, { getState }) => {
    const appState = getState();
    const currentPage = getCurrentPage(appState);
    const size = getResultsPerPage(appState);
    const selectedColumn: ColumnSelected = getSelectedColumn(appState);
    const searchValue = getFilterSearch(appState);
    const username = getUsername(appState);
    const needsSource = getSelectedNeedsSource(appState);
    const sortOrder = selectedColumn.isAscending ? Sort.ASC : Sort.DESC;
    const sort = `{${selectedColumn.key}:${sortOrder}}`;
    // build search value to serch by code description provider_tier_1_name provider_tier_1_id inbounder
    //{code:LK:or|OR|provider_code:LK:or|OR|description:LK:or}
    let search: string = '';
    searchValue.split('+').forEach((searchSplitValue) => {
      if (searchSplitValue !== '') {
        search += `${NameValues.CODE}:LK:${searchSplitValue}
        |OR|${NameValues.INBOUNDER}:LK:${searchSplitValue}
        |OR|${NameValues.DESCRIPTION}:LK:${searchSplitValue}
        |OR|${'provider_tier_1_name'}:LK:${searchSplitValue}
        |OR|${'provider_tier_1_id'}:LK:${searchSplitValue};`; // Asegúrate de que el punto y coma esté aquí
      }
    });

    let querySearch: string[] = getSelectedFiltersQueryArray(appState);

    querySearch.push(search);
    const query = querySearch.filter((item) => item !== '').join('||');

    const queryParams = {
      page: currentPage,
      size,
      sort: selectedColumn.key.length ? sort : '',
      search: query && `{${query}}`,
      needs_source: needsSource
    };

    const response = await getJitDataService.execute({
      queryParams,
      username
    });
    return response;
  }
);

const downloadJitStock = createAsyncThunk(
  //'partCoverageData/downloadJitStock',
  'PartCoverageProgramCoverage/downloadJitStock',
  async (_, { getState }) => {
    const appState = getState();
    const username = getUsername(appState);
    const searchValue = getFilterSearch(appState);
    // build search value to serch by code description provider_tier_1_name provider_tier_1_id inbounder
    //{code:LK:or|OR|provider_code:LK:or|OR|description:LK:or}
    let search: string = '';
    searchValue.split('+').forEach((searchSplitValue) => {
      if (searchSplitValue !== '') {
        search += `${NameValues.CODE}:LK:${searchSplitValue}
        |OR|${NameValues.INBOUNDER}:LK:${searchSplitValue}
        |OR|${NameValues.DESCRIPTION}:LK:${searchSplitValue}
        |OR|${'provider_tier_1_name'}:LK:${searchSplitValue}
        |OR|${'provider_tier_1_id'}:LK:${searchSplitValue};`; // Asegúrate de que el punto y coma esté aquí
      }
    });

    let querySearch: string[] = getSelectedFiltersQueryArray(appState);

    querySearch.push(search);
    const query = querySearch.filter((item) => item !== '').join('||');

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const queryParams = {
      tz: timeZone,
      search: query && `{${query}}`
    };
    const response = await DownloadJitStockUseCase.execute({
      queryParams,
      username
    });
    return response;
  }
);

const sendComment = createAsyncThunk(
  'PartCoverageProgramCoverage/sendComment',
  async (commentData: { code: string; text: string }) => {
    const response = await EditCommentUseCase.execute({
      code: commentData.code,
      body: {
        text: commentData.text
      }
    });
    return response;
  }
);

const getJitLoadsByPiece = createAsyncThunk(
  'jitData/getJitLoadsByPiece',
  async (_, { getState }) => {
    try {
      const appState = getState();
      let selectedPiece = getSelectedPiece(appState);
      let selectedProviderTier = getSelectedProviderTier(appState);
      let selectedConType = getSelectedConType(appState);
      const today = new Date();
      const futureDate = addDays(today, 15);

      let todayFormatted = format(today, 'yyyy-MM-dd');
      let toFormatted = format(futureDate, 'yyyy-MM-dd');
      const queryParams = {
        code: selectedPiece,
        provider_id: selectedProviderTier,
        provider_tier: selectedConType,
        from_date: todayFormatted,
        to_date: toFormatted
      };

      const response = await GetTransitsService.execute({ queryParams });

      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in getJitLoadsByPiece:', error);
      throw error;
    }
  }
);

const deleteTransit = createAsyncThunk(
  'jitData/deleteTransit',
  async (queryParams: { code: string; delivery_date: string }) => {
    const USE_DELETE_TRANSIT_MOCK = false;
    if (USE_DELETE_TRANSIT_MOCK) {
      return Promise.resolve({
        success: true,
        queryParams
      });
    }
    const response = await DeleteTransitsService.execute({
      queryParams
    });

    return response;
  }
);
const editTransit = createAsyncThunk(
  'jitData/editTransit',
  async (transit: LoadDataEdit) => {
    const respond = await EditTransitsService.execute({
      body: LoadMapper.toDomain(transit)
    });
    const fechaFormated = parseISO(respond.delivery_time);
    const formatedTransit = {
      id: respond.id,
      amount: respond.amount,
      deliveryTime: `${GetDateFormatedService.getTimeFormated(
        respond.delivery_time
      )}h`,
      deliveryTimeDate: format(fechaFormated, 'dd/MM/yyyy'),
      providerDeliveryNote:
        respond.provider_delivery_note !== ''
          ? respond.provider_delivery_note
          : '-',
      plateNumber: respond.plate_number
    };
    return formatedTransit;
  }
);

const createTransit = createAsyncThunk<
  loadTransists, // fulfilled
  LoadData, // argumento
  { rejectValue: string } // rejected
>('jitData/createTransit', async (data: LoadData, { rejectWithValue }) => {
  try {
    const response = await CreateJitTransitService.execute({
      body: data
    });

    return LoadMapper.toTransit(response.data);
  } catch (error: unknown) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }

    return rejectWithValue('Unexpected error');
  }
});

const getRemarkByPiece = createAsyncThunk(
  'jitData/getRemarkByPiece',
  async (_, { getState }) => {
    try {
      const appState = getState();
      const selectedPiece = getSelectedPiece(appState);
      const selectedProviderTierId = getSelectedProviderTier(appState);
      const selectedConType = getSelectedConType(appState);
      const today = new Date();

      let todayFormatted = format(today, 'yyyy-MM-dd');
      const queryParams = {
        code: selectedPiece,
        provider_id: selectedProviderTierId,
        provider_tier: selectedConType,
        fromDate: todayFormatted
      };

      const response = await GetRemarksService.execute(queryParams);

      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in getRemarkByPiece:', error);
      throw error;
    }
  }
);

const createRemark = createAsyncThunk(
  'jitData/createRemark',
  async (data: RemarkData) => {
    const response = await CreateJitRemarkService.execute({
      body: data
    });

    return RemarkMapper.toDomain(response.data);
  }
);

const deleteRemark = createAsyncThunk(
  'jitData/deleteRemark',
  async (queryParams: { id: number }) => {
    const response = await DeleteJitRemarksByIdService.execute({
      queryParams
    });

    return response;
  }
);

const editRemark = createAsyncThunk(
  'jitData/editRemark',
  async (remark: RemarkData) => {
    return EditJitRemarkService.execute({
      body: remark
    });
  }
);

const downloadRemark = createAsyncThunk(
  'jitData/downloadRemark',
  async (queryParams: { id: number }) => {
    const response = await DownloadJitRemarkService.execute({
      queryParams
    });

    return response;
  }
);

const downloadM100 = createAsyncThunk(
  //'partCoverageData/downloadM100',
  'PartCoverageProgramCoverage/downloadM100',
  async (queryParams: {
    code: string;
    providerId: number;
    providerTier: string;
  }) => {
    const response = await DownloadM100UseCase.execute({
      queryParams
    });
    return response;
  }
);
const getStockStatus = createAsyncThunk(
  'jitData/getStockStatus',
  async (_, { getState }) => {
    try {
      const appState = getState();
      const selectedPiece = getSelectedPiece(appState);
      const selectedProviderTierId = getSelectedProviderTier(appState);
      const selectedConType = getSelectedConType(appState);
      const queryParams = {
        code: selectedPiece,
        providerId: selectedProviderTierId,
        providerTier: selectedConType
      };

      const response = await GetStockStatusService.execute({ queryParams });

      return response.stockStatus;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in getRemarkByPiece:', error);
      throw error;
    }
  }
);

export {
  getPartCoverage,
  downloadJitStock,
  sendComment,
  getJitLoadsByPiece,
  deleteTransit,
  editTransit,
  getRemarkByPiece,
  createRemark,
  deleteRemark,
  editRemark,
  downloadRemark,
  createTransit,
  downloadM100,
  getStockStatus
};
